const GSI_SCRIPT_URL = "https://accounts.google.com/gsi/client";

let loadPromise: Promise<void> | null = null;

function waitForGoogleApi(timeoutMs = 15000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts) {
      resolve();
      return;
    }

    const started = Date.now();
    const tick = () => {
      if (window.google?.accounts) {
        resolve();
      } else if (Date.now() - started > timeoutMs) {
        reject(new Error("Google Sign-In timed out. Please refresh and try again."));
      } else {
        setTimeout(tick, 50);
      }
    };
    tick();
  });
}

export function getGoogleClientId(): string | null {
  const clientId = process.env["REACT_APP_GOOGLE_CLIENT_ID"];
  if (!clientId) return null;

  const isValidFormat =
    clientId.endsWith(".apps.googleusercontent.com") && clientId.length > 50;
  return isValidFormat ? clientId : null;
}

export function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    const existing = document.querySelector(
      'script[src*="accounts.google.com/gsi/client"]',
    );

    if (!existing) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = GSI_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () =>
          reject(new Error("Failed to load Google Sign-In."));
        document.head.appendChild(script);
      });
    }

    await waitForGoogleApi();
  })().catch((err) => {
    loadPromise = null;
    throw err;
  });

  return loadPromise;
}

export type GoogleButtonText = "signup_with" | "signin_with" | "continue_with";

export interface RenderGoogleButtonOptions {
  elementId: string;
  text?: GoogleButtonText;
  width?: number | string;
}

export function initializeGoogleAuth(callback: (response: any) => void): void {
  const clientId = getGoogleClientId();
  if (!clientId || !window.google?.accounts) {
    throw new Error("Google OAuth is not properly configured.");
  }

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback,
    auto_select: false,
    cancel_on_tap_outside: true,
  });
}

export function renderGoogleButton({
  elementId,
  text = "signup_with",
  width = 400,
}: RenderGoogleButtonOptions): void {
  const element = document.getElementById(elementId);
  if (!element || !window.google?.accounts) {
    throw new Error("Google Sign-In button container not found.");
  }

  element.innerHTML = "";
  window.google.accounts.id.renderButton(element, {
    theme: "outline",
    size: "large",
    text,
    width,
    shape: "pill",
    logo_alignment: "left",
  });
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: any) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          prompt: (callback: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
      };
    };
  }
}
